/* eslint-disable no-param-reassign -- gate entries and three.js objects are mutable records updated in place */
import * as THREE from 'three'
import type { Reflector } from 'three/examples/jsm/objects/Reflector'

import { defaultReflectorGateOptions, estimateTargetBytes } from './options'
import {
  clearReflectorEntry,
  getPreparedReflector,
  prepareReflector,
  restoreReflector,
  setReflectorEntry,
} from './prepare-reflector'
import { createTargetPool } from './target-pool'
import type {
  Entry,
  EntryHost,
  ReflectorGateOptions,
  ReflectorRegistration,
} from './types'
import {
  computeVisible,
  createVisibilityScratch,
  needsRender,
  snapshot,
  updateFrustum,
} from './visibility'

export interface ReflectorGate extends EntryHost {
  readonly options: ReflectorGateOptions
  configure: (options: Partial<ReflectorGateOptions>) => void
  getResidentBytes: () => number
  invalidate: () => void
  exclude: (object: THREE.Object3D) => () => void
  register: (
    reflector: Reflector,
    registration: ReflectorRegistration
  ) => () => void
  runFrame: (camera: THREE.Camera, frameKey: number) => void
}

const byStalenessThenDistance = (a: Entry, b: Entry) =>
  b.framesSinceRender - a.framesSinceRender ||
  a.distanceToCamera - b.distanceToCamera

/**
 * Per-renderer scheduler for planar mirrors. Each frame it decides which
 * mirrors are visible, hands out render targets through the target pool,
 * and picks which stale mirrors may re-render within the pass budget.
 */
export function createReflectorGate(
  overrides: Partial<ReflectorGateOptions> = {}
): ReflectorGate {
  const options: ReflectorGateOptions = {
    ...defaultReflectorGateOptions(),
    ...overrides,
  }
  const pool = createTargetPool(options)
  const entries = new Map<Reflector, Entry>()
  const excludedObjects = new Set<THREE.Object3D>()
  const scratch = createVisibilityScratch()

  let sceneVersion = 0
  let frameCounter = 0
  let lastRegistrationFrame = -Infinity
  let lastFrameKey: number | undefined

  const trackHidden = (entry: Entry) => {
    entry.framesHidden += 1
    if (entry.owner && entry.framesHidden > options.evictAfterFrames) {
      pool.release(entry)
    }
  }

  const isDueForRender = (entry: Entry, camera: THREE.Camera) => {
    if (!needsRender(entry, camera, sceneVersion)) return false

    const interval = entry.getUpdateInterval()
    if (interval <= 1) return true

    const current = entry.intervalCounter
    entry.intervalCounter += 1
    return current % interval === 0
  }

  const grantPasses = (stale: Entry[]) => {
    if (stale.length === 0) return
    stale.sort(byStalenessThenDistance)
    const budget = Math.max(0, Math.floor(options.maxPassesPerFrame))
    const grant = Math.min(budget, stale.length)
    for (let i = 0; i < grant; i += 1) {
      stale[i].allowed = true
    }
  }

  const gate: ReflectorGate = {
    options,
    configure(next) {
      Object.assign(options, next)
    },
    getResidentBytes: () => pool.getResidentBytes(),
    invalidate() {
      sceneVersion += 1
    },
    exclude(object) {
      excludedObjects.add(object)
      return () => {
        excludedObjects.delete(object)
      }
    },
    register(reflector, registration) {
      prepareReflector(reflector, registration.fallbackColor)
      const record = getPreparedReflector(reflector)
      if (!record) throw new Error('Reflector was not prepared')
      const entry: Entry = {
        ...registration,
        host: gate,
        reflector,
        reflectorMaterial: record.reflectorMaterial,
        fallbackMaterial: record.fallbackMaterial,
        currentSize: reflector.getRenderTarget().width,
        targetBytes: estimateTargetBytes(
          registration.textureSize,
          registration.textureSize,
          registration.samples
        ),
        lastCameraWorld: new THREE.Matrix4(),
        lastCameraProjection: new THREE.Matrix4(),
        lastReflectorWorld: new THREE.Matrix4(),
        lastSceneVersion: -1,
        hasSnapshot: false,
        owner: false,
        renderedOnce: false,
        intervalCounter: 0,
        framesHidden: 0,
        framesSinceRender: 0,
        distanceToCamera: 0,
        visible: true,
        allowed: false,
      }
      entries.set(reflector, entry)
      setReflectorEntry(reflector, entry)
      pool.applyMaterial(entry)
      lastRegistrationFrame = frameCounter

      return () => {
        pool.release(entry)
        entries.delete(reflector)
        clearReflectorEntry(reflector)
        restoreReflector(reflector)
      }
    },
    runFrame(camera, frameKey) {
      if (frameKey === lastFrameKey) return
      lastFrameKey = frameKey
      frameCounter += 1

      updateFrustum(scratch, camera)

      const waiting: Entry[] = []
      const owners: Entry[] = []
      const stale: Entry[] = []

      entries.forEach((entry) => {
        entry.allowed = false
        entry.framesSinceRender += 1

        entry.visible = computeVisible(scratch, entry, camera)
        entry.reflector.visible = entry.visible

        if (!entry.visible) {
          trackHidden(entry)
          if (entry.owner) owners.push(entry)
          return
        }
        entry.framesHidden = 0
        if (!entry.owner) {
          waiting.push(entry)
          return
        }
        owners.push(entry)
        if (isDueForRender(entry, camera)) stale.push(entry)
      })

      if (frameCounter - lastRegistrationFrame >= options.warmupFrames) {
        pool.assign(waiting, owners)
      }

      grantPasses(stale)
    },
    hideDuringPass(hide) {
      entries.forEach((other) => hide(other.reflector))
      excludedObjects.forEach(hide)
    },
    snapshot(entry, camera) {
      snapshot(entry, camera, sceneVersion)
    },
  }

  return gate
}

const gates = new WeakMap<object, ReflectorGate>()

export function getReflectorGate(renderer: object): ReflectorGate {
  let gate = gates.get(renderer)
  if (!gate) {
    gate = createReflectorGate()
    gates.set(renderer, gate)
  }
  return gate
}
